import Feature from "ol/Feature";
import Source from "ol/source/Vector";
import { IUndoRedo } from "../types";

type EAction = "Insert" | "Update" | "Delete";

let UndoRedoId:number = 1; // global id

interface IStack {
  action: EAction;
  feature: Feature;
}

export class UndoRedo {
  // a stack may contain one or more than one of features
  // that were inserted/updated/deleted from a single action
  // such as split feature that will delete original feature
  // and create at least two new features
  private undoStacks: IStack[][] = [];
  private redoStacks: IStack[][] = [];
  private controls: IUndoRedo = {
    undo: {isEnabled:false},
    redo: {isEnabled:false},
    save: {isEnabled:false}
  };

  constructor(controls: IUndoRedo) {
    if (controls) {
      this.controls = controls;
      for (let c in controls) {
        controls[c].isEnabled = false;
      }
      this.controls.undo.do = this.undo;
      this.controls.redo.do = this.redo;
    }
  };

  clear() {
    this.undoStacks = [];
    this.redoStacks = [];
    this.controls.undo.isEnabled = false;
    this.controls.redo.isEnabled = false;
    this.controls.save.isEnabled = false;
  }

  updateSaveState() {
    // todo:
    // this does not perfect yet. when newly inserted feeature is deleted,
    // the save button is still enabled, but nothing will actually saved to db.
    let enabled = false;
    if (this.undoStacks.length>0) {
      let stacks:IStack[], stack:IStack;
      for (let i=0; i<this.undoStacks.length; i++) {
        stacks = this.undoStacks[i];
        for (let j=0; j<stacks.length; j++) {
          enabled = !!stacks[j].action.match(/Insert|Update/i) ||
                    (stacks[j].action=='Delete' && !!stacks[j].feature.getId());
          if (enabled) {
            break;
          }
        }
        if (enabled) {
          break;
        }
      }
    }
    this.controls.save.isEnabled = enabled;
  }

  private removeFeatureFromStacks(feature:Feature,action:EAction,undoRedoStacks:IStack[][]) : boolean {
    let i, j;
    let stacks:IStack[], stack:IStack;
    let done = false;
    for (i=0; i<undoRedoStacks.length; i++) {
      stacks = undoRedoStacks[i];
      for (j=0; j<stacks.length; j++) {
        stack = stacks[j];
        //if (stack.action==action && 
        //    feature.getId()==stack.feature.getId() &&
        if (feature['ol_uid']==stack.feature['ol_uid']) {
          stacks.splice(j,1);
          done = true;
          break;
        }
      }
      if (done && stacks.length==0) {
        undoRedoStacks.splice(i,1);
        i--;
      }
    }
    return done;
  }

  removeFeature(feature:Feature, action:EAction) {
    //let done = this.removeFeatureFromStacks(feature, action, this.undoStacks);
    //if (!done) {
    //  this.removeFeatureFromStacks(feature, action, this.redoStacks);
    //}
    [this.undoStacks,this.redoStacks].forEach((stacks) => {
      this.removeFeatureFromStacks(feature, action, stacks);
    });
    this.controls.redo.isEnabled = (this.redoStacks.length>0);
    this.controls.undo.isEnabled = (this.undoStacks.length>0);
    this.controls.save.isEnabled = this.controls.undo.isEnabled;
    //this.updateSaveState();
  }

  cloneFeature(feature:Feature) : Feature {
    let f = null;
    //let p, props;
    if (feature) {
      //props = feature.getProperties();
      f = feature.clone();
      //for (p in props) {
      //  f.set(p,props[p]);
      //}
      f.setId(feature.getId());
      f['ol_uid'] = feature['ol_uid'];
      f['NATIVE_SOURCE'] = feature['NATIVE_SOURCE'];
    }
		return f;
  };
  
  getPairFromSource(source:Source, feature:Feature) {
    let f:Feature;
    let id = feature.getId();
    if (!!id) {
      f = source.getFeatureById(id);
    } else if (feature['ol_uid']) {
      id = feature['ol_uid'];
      if (source['getFeatureByUid']) {
        f = source['getFeatureByUid'](id);
        //f = source['uidIndex_'][id]; //same as above
      } else if (source['undefIdIndex_']) {
        f = source['undefIdIndex_'][id];
      }
    }
    return f;
  }
	
	addUndo(feature:Feature, action:EAction, append?:boolean) {
    if (!feature) {
      return;
    }
    let f = this.cloneFeature(feature);
    if (action=='Insert') {
      f.set('STATE', 'Delete');
    }
    let stack = {
      action: action,
      feature: f
    };
    if (!this.undoStacks) {
      this.undoStacks = [[stack]];
    } else {
      if (append===true) {
        let stacks = this.undoStacks[this.undoStacks.length-1];
        stacks.push(stack);
      } else {
        this.undoStacks.push([stack]);
      }
    }
    this.controls.undo.isEnabled = true;
    this.controls.save.isEnabled = true;
    //this.updateSaveState();
    feature.set('STATE', action);
	}
	
	/**
	 * Method: onInsert
	 * event haldler for featureadded 
	 */
	onInsert(feature:Feature) {
		this.addUndo(feature, "Insert");
	}
	
	/**
	 * Method: onDelete
	 * event haldler for beforefeatureremoved 
	 */
	onDelete(feature:Feature) {
		this.addUndo(feature, "Delete");
	}
	
	/**
	 * Method: onUpdate
	 * event haldler for beforefeaturemodified
	 */
	onUpdate(feature:Feature) {
		this.addUndo(feature, "Update");
  }
  
	/**
	 * Method: addUndos
	 * these features were changed from one action, such as split polygon
	 */
	addUndos(features:Feature[], action:EAction[]){
    features.forEach((feature, index) => {
      this.addUndo(feature, action[index], index>0);
    }, this);
	}
	
	/**
	 * Method: getUndoData
	 * returns the last edited data
	 */ 
	getUndo() : IStack[] {
		let stacks = this.undoStacks.pop();
    this.controls.undo.isEnabled = (this.undoStacks.length>0);
    this.controls.save.isEnabled = this.controls.undo.isEnabled;
    //this.updateSaveState();
		return stacks;
	}
	
	/* Method: getRedoData
	 * returns the last redo data
	 */
	getRedo() : IStack[] {	
		let stacks = this.redoStacks.pop();
		this.controls.redo.isEnabled = (this.redoStacks.length>0);
		return stacks;
	}
	
	undo() {
    let stacks:IStack[] = this.getUndo();
    stacks.forEach((stack) => {
      let source:Source = stack.feature['NATIVE_SOURCE'];
      let feature = this.getPairFromSource(source, stack.feature);
      let f = this.cloneFeature(feature);
      let p, props = feature.getProperties();
      // clear its properties
      for (p in props) {
        feature.unset(p, true);
      }
      //restore properties and geometry
      feature.setProperties(stack.feature.getProperties());
      feature.setGeometry(stack.feature.getGeometry());
      /*if (stack.action=='Insert') {
        feature.set('STATE', 'Delete');
      }*/
      stack.feature = f;
    }, this);
    this.redoStacks = this.redoStacks || [];
		this.redoStacks.push(stacks);
		this.controls.redo.isEnabled = true;
	}
	
	redo() {
    let stacks:IStack[] = this.getRedo();
    stacks.forEach((stack) => {
      let source:Source = stack.feature['NATIVE_SOURCE'];
      let feature = this.getPairFromSource(source, stack.feature);
      let f = this.cloneFeature(feature);
      let p, props = feature.getProperties();
      // clear its properties
      for (p in props) {
        feature.unset(p, true);
      }
      //restore properties and geometry
      feature.setProperties(stack.feature.getProperties());
      feature.setGeometry(stack.feature.getGeometry());
      stack.feature = f;
    }, this);

		this.undoStacks.push(stacks);
    this.controls.undo.isEnabled = true;
    this.controls.save.isEnabled = this.controls.undo.isEnabled;
    //this.updateSaveState();
	}
}